/**
 * usePageBrowser
 *
 * State management hook for the Page Browser Modal.
 * Handles folder organization, starring, search/filter, sort, and page switching.
 */

import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEditorStore } from '@/stores/editor/editor-store';
import type { Page, NavigationGroup } from '@/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PageSortBy = 'name' | 'order' | 'updatedAt' | 'componentCount';

export interface UsePageBrowserReturn {
  // Modal state
  isOpen: boolean;
  openModal: () => void;
  closeModal: () => void;

  // Data
  allPages: Page[];
  filteredPages: Page[];
  navigationGroups: NavigationGroup[];
  starredCount: number;
  totalCount: number;

  // Folder counts
  folderPageCounts: Record<string, number>;
  uncategorizedCount: number;

  // Filters
  selectedFolderId: string | null;
  searchQuery: string;
  sortBy: PageSortBy;
  sortOrder: 'asc' | 'desc';

  // Filter setters
  setSelectedFolderId: (id: string | null) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sort: PageSortBy) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;

  // Page actions
  toggleStar: (pageId: string) => void;
  switchToPage: (pageId: string) => void;

  // Folder actions
  assignToFolder: (pageId: string, groupId: string | undefined) => void;
  createFolder: (name: string) => void;
  renameFolder: (groupId: string, name: string) => void;
  deleteFolder: (groupId: string) => void;

  // Current page context
  currentPageId: string | undefined;
  getPageComponentCount: (pageId: string) => number;
}

// Special folder IDs
export const FOLDER_ALL = null;
export const FOLDER_STARRED = '__starred__';
export const FOLDER_UNCATEGORIZED = '';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePageBrowser(): UsePageBrowserReturn {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  // Editor store selectors
  const pages = useEditorStore((state) => state.pages);
  const currentPageId = useEditorStore((state) => state.currentPageId);
  const project = useEditorStore((state) => state.project);
  const updatePage = useEditorStore((state) => state.updatePage);
  const storeSwitchToPage = useEditorStore((state) => state.switchToPage);
  const syncCurrentPageComponents = useEditorStore((state) => state.syncCurrentPageComponents);
  const getPageComponentCount = useEditorStore((state) => state.getPageComponentCount);
  const addNavigationGroup = useEditorStore((state) => state.addNavigationGroup);
  const updateNavigationGroup = useEditorStore((state) => state.updateNavigationGroup);
  const deleteNavigationGroup = useEditorStore((state) => state.deleteNavigationGroup);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);

  // Filter state
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(FOLDER_ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<PageSortBy>('order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Derived data
  const allPages = useMemo(() => {
    if (!pages) return [];
    return Array.isArray(pages) ? pages : [];
  }, [pages]);

  const navigationGroups = useMemo(() => {
    return project?.navigation?.navigationGroups ?? [];
  }, [project?.navigation?.navigationGroups]);

  const starredCount = useMemo(() => {
    return allPages.filter((p) => p.isStarred).length;
  }, [allPages]);

  const folderPageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const group of navigationGroups) {
      counts[group.id] = 0;
    }
    for (const page of allPages) {
      const gid = page.navigation?.navigationGroup;
      if (gid && counts[gid] !== undefined) {
        counts[gid]++;
      }
    }
    return counts;
  }, [allPages, navigationGroups]);

  const uncategorizedCount = useMemo(() => {
    const groupIds = new Set(navigationGroups.map((g) => g.id));
    return allPages.filter((p) => {
      const gid = p.navigation?.navigationGroup;
      return !gid || !groupIds.has(gid);
    }).length;
  }, [allPages, navigationGroups]);

  // Filtered and sorted pages
  const filteredPages = useMemo(() => {
    let result = [...allPages];

    // Filter by folder
    if (selectedFolderId === FOLDER_STARRED) {
      result = result.filter((p) => p.isStarred);
    } else if (selectedFolderId === FOLDER_UNCATEGORIZED) {
      const groupIds = new Set(navigationGroups.map((g) => g.id));
      result = result.filter((p) => {
        const gid = p.navigation?.navigationGroup;
        return !gid || !groupIds.has(gid);
      });
    } else if (selectedFolderId && selectedFolderId !== FOLDER_STARRED) {
      result = result.filter(
        (p) => p.navigation?.navigationGroup === selectedFolderId,
      );
    }
    // FOLDER_ALL (null) = no filtering

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.description && p.description.toLowerCase().includes(q)),
      );
    }

    // Sort
    result.sort((a, b) => {
      let cmp = 0;
      switch (sortBy) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'order':
          cmp = (a.order || 0) - (b.order || 0);
          break;
        case 'updatedAt':
          cmp =
            new Date(a.updatedAt || 0).getTime() -
            new Date(b.updatedAt || 0).getTime();
          break;
        case 'componentCount':
          cmp = getPageComponentCount(a.id) - getPageComponentCount(b.id);
          break;
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [allPages, selectedFolderId, searchQuery, sortBy, sortOrder, navigationGroups, getPageComponentCount]);

  // Actions
  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
  }, []);

  const toggleStar = useCallback(
    (pageId: string) => {
      const page = allPages.find((p) => p.id === pageId);
      if (page) {
        updatePage(pageId, { isStarred: !page.isStarred });
      }
    },
    [allPages, updatePage],
  );

  const handleSwitchToPage = useCallback(
    (pageId: string) => {
      if (pageId !== currentPageId && projectId) {
        syncCurrentPageComponents();
        storeSwitchToPage(pageId);
        navigate(`/editor/${projectId}/page/${pageId}`);
      }
      closeModal();
    },
    [currentPageId, projectId, syncCurrentPageComponents, storeSwitchToPage, navigate, closeModal],
  );

  const assignToFolder = useCallback(
    (pageId: string, groupId: string | undefined) => {
      const page = allPages.find((p) => p.id === pageId);
      if (page) {
        updatePage(pageId, {
          navigation: { ...page.navigation, navigationGroup: groupId },
        });
      }
    },
    [allPages, updatePage],
  );

  const handleCreateFolder = useCallback(
    (name: string) => {
      addNavigationGroup({
        name,
        order: navigationGroups.length,
        collapsed: false,
        pageIds: [],
      });
    },
    [addNavigationGroup, navigationGroups.length],
  );

  const handleRenameFolder = useCallback(
    (groupId: string, name: string) => {
      updateNavigationGroup(groupId, { name });
    },
    [updateNavigationGroup],
  );

  const handleDeleteFolder = useCallback(
    (groupId: string) => {
      deleteNavigationGroup(groupId);
      // If the deleted folder was selected, reset to All
      if (selectedFolderId === groupId) {
        setSelectedFolderId(FOLDER_ALL);
      }
    },
    [deleteNavigationGroup, selectedFolderId],
  );

  return {
    isOpen,
    openModal,
    closeModal,

    allPages,
    filteredPages,
    navigationGroups,
    starredCount,
    totalCount: allPages.length,

    folderPageCounts,
    uncategorizedCount,

    selectedFolderId,
    searchQuery,
    sortBy,
    sortOrder,

    setSelectedFolderId,
    setSearchQuery,
    setSortBy,
    setSortOrder,

    toggleStar,
    switchToPage: handleSwitchToPage,

    assignToFolder,
    createFolder: handleCreateFolder,
    renameFolder: handleRenameFolder,
    deleteFolder: handleDeleteFolder,

    currentPageId,
    getPageComponentCount,
  };
}
